import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  User, 
  Calendar, 
  PoundSterling, 
  MessageSquare,
  FileText,
  CheckCircle,
  Clock,
  Wrench,
  Home,
  LogOut,
  ChevronRight,
  Loader2,
  Camera,
  Upload,
  AlertCircle,
  Gift,
  History,
  X,
  Users,
  Phone,
  Mail,
  Send,
  ArrowLeft,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status timeline configuration
const STATUS_TIMELINE = [
  { key: "new_lead", label: "Quote Received", icon: FileText },
  { key: "site_visit_scheduled", label: "Site Visit Booked", icon: Calendar },
  { key: "quote_sent", label: "Quote Sent", icon: FileText },
  { key: "job_won", label: "Work Scheduled", icon: Wrench },
  { key: "in_progress", label: "Work In Progress", icon: Wrench },
  { key: "job_completed", label: "Completed", icon: CheckCircle }
];

const getStatusIndex = (status) => {
  const statusMap = {
    "pending": 0, "new_lead": 0, "new_enquiry": 0, "qualified": 0,
    "site_visit_scheduled": 1, "booked": 1, "quote_booked": 1,
    "quote_sent": 2, "quoted": 2,
    "job_won": 3, "work_scheduled": 3,
    "in_progress": 4, "work_in_progress": 4,
    "job_completed": 5, "completed": 5
  };
  return statusMap[status?.toLowerCase()] ?? 0;
};

// Detail Modal Component
const DetailModal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4 overflow-y-auto max-h-[calc(90vh-60px)]">
        {children}
      </div>
    </div>
  </div>
);

// Bank Transfer Payment Instructions
const BankTransferDetails = ({ amount, reference }) => {
  const [bank, setBank] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/bank-settings`)
      .then(r => setBank(r.data))
      .catch(() => setBank({}));
  }, []);
  if (!bank) return null;
  const Row = ({ label, value }) => (
    <div className="flex justify-between py-1.5 border-b last:border-0 border-blue-100">
      <span className="text-sm text-gray-700">{label}</span>
      <span className="font-mono font-semibold text-gray-900">{value || "—"}</span>
    </div>
  );
  return (
    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200" data-testid="bank-transfer-details">
      <p className="font-semibold text-blue-900 mb-2">Preferred Payment Method: Bank Transfer</p>
      <Row label="Account Name" value={bank.account_name} />
      <Row label="Sort Code" value={bank.sort_code} />
      <Row label="Account Number" value={bank.account_number} />
      {bank.bank_name && <Row label="Bank" value={bank.bank_name} />}
      <Row label="Reference" value={reference || "Use your invoice number"} />
      {amount != null && <Row label="Amount" value={`£${Number(amount).toFixed(2)}`} />}
      {bank.payment_instructions && (
        <p className="text-xs text-blue-800 mt-3">{bank.payment_instructions}</p>
      )}
      {!bank.configured && (
        <p className="text-xs text-amber-700 mt-2">Bank details not yet configured. Please contact us for payment instructions.</p>
      )}
    </div>
  );
};

// Referral Form Component
const ReferralForm = ({ onSubmit, onCancel, submitting }) => {
  const [form, setForm] = useState({
    referral_name: "",
    referral_phone: "",
    referral_email: "",
    notes: ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.referral_name || !form.referral_phone || !form.referral_email) {
      toast.error("Please fill in all required fields");
      return;
    }
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Friend's Name *</Label>
        <Input
          value={form.referral_name}
          onChange={(e) => setForm({ ...form, referral_name: e.target.value })}
          placeholder="John Smith"
          className="mt-1"
        />
      </div>
      <div>
        <Label>Phone Number *</Label>
        <Input
          value={form.referral_phone}
          onChange={(e) => setForm({ ...form, referral_phone: e.target.value })}
          placeholder="07xxx xxxxxx"
          className="mt-1"
        />
      </div>
      <div>
        <Label>Email Address *</Label>
        <Input
          type="email"
          value={form.referral_email}
          onChange={(e) => setForm({ ...form, referral_email: e.target.value })}
          placeholder="friend@email.com"
          className="mt-1"
        />
      </div>
      <div>
        <Label>Notes (optional)</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Any details about their project..."
          rows={2}
          className="mt-1"
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting} className="flex-1">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Submit Referral
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
};

const CustomerPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Modal states
  const [showQuoteDetail, setShowQuoteDetail] = useState(null);
  const [showPaymentDetail, setShowPaymentDetail] = useState(null);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(null);
  
  // Quote history
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Referrals
  const [referrals, setReferrals] = useState({ referrals: [], counts: {}, rewards: {} });
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [submittingReferral, setSubmittingReferral] = useState(false);

  const customerName = localStorage.getItem("customer_name") || "Customer";
  const customerEmail = localStorage.getItem("customer_email") || "";

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("customer_token")}`
  };

  useEffect(() => {
    const token = localStorage.getItem("customer_token");
    if (!token) {
      navigate("/customer-login");
      return;
    }
    fetchDashboard();
    fetchReferrals();
  }, [navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/customer/dashboard`, { headers });
      setDashboard(response.data);
      if (response.data.projects?.length > 0) {
        setSelectedProject(response.data.projects[0]);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("customer_token");
        navigate("/customer-login");
      }
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/customer/referrals`, { headers });
      setReferrals(response.data);
    } catch (error) {
      console.error("Error fetching referrals:", error);
    }
  };

  const fetchQuoteHistory = async (projectId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/customer/project/${projectId}/quote-history`, { headers });
      setQuoteHistory(response.data.versions || []);
    } catch (error) {
      console.error("Error fetching quote history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("customer_token");
    localStorage.removeItem("customer_name");
    localStorage.removeItem("customer_email");
    navigate("/customer-login");
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedProject) return;

    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/customer/project/${selectedProject.id}/message`,
        { message: newMessage },
        { headers }
      );
      toast.success("Message sent!");
      setNewMessage("");
      fetchDashboard();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSubmitReferral = async (form) => {
    setSubmittingReferral(true);
    try {
      await axios.post(`${API}/customer/referrals`, form, { headers });
      toast.success("Referral submitted! You'll earn £100 when they book.");
      setShowReferralForm(false);
      fetchReferrals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit referral");
    } finally {
      setSubmittingReferral(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not scheduled";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const formatCurrency = (amount) => {
    return `£${(amount || 0).toLocaleString()}`;
  };

  useEffect(() => {
    if (selectedProject && activeTab === "history") {
      fetchQuoteHistory(selectedProject.id);
    }
  }, [selectedProject, activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-gray-600 mt-4">Loading your portal...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: Home },
    { id: "quotes", label: "Quotes", icon: FileText },
    { id: "payments", label: "Payments", icon: PoundSterling },
    { id: "messages", label: "Messages", icon: MessageSquare },
    { id: "history", label: "Quote History", icon: History },
    { id: "referrals", label: "Referrals", icon: Gift }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold text-primary font-montserrat">
                DC Tiling
              </Link>
              <span className="text-gray-400 hidden sm:inline">|</span>
              <span className="text-gray-600 hidden sm:inline">Customer Portal</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">{customerName}</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, {customerName}</h1>
          <p className="text-gray-600">Manage your projects, payments, and referrals</p>
        </div>

        {dashboard?.projects?.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Projects Found</h2>
            <p className="text-gray-600 mb-4">
              You don't have any active projects yet. Once you get a quote, your project will appear here.
            </p>
            <Link to="/estimate">
              <Button className="bg-primary hover:bg-primary/90">
                Get a Quote
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Sidebar - Project List & Quick Actions */}
            <div className="lg:col-span-1 space-y-4">
              {/* Project Selector */}
              <div className="bg-white rounded-2xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Your Projects</h3>
                <div className="space-y-2">
                  {dashboard?.projects?.map(project => (
                    <button
                      key={project.id}
                      onClick={() => { setSelectedProject(project); setActiveTab("overview"); }}
                      className={`w-full text-left p-3 rounded-xl transition-all ${
                        selectedProject?.id === project.id
                          ? "bg-primary/10 border-2 border-primary"
                          : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
                      }`}
                      data-testid={`project-${project.id}`}
                    >
                      <p className="font-medium text-gray-900 text-sm">
                        {project.project_type?.replace(/_/g, " ") || "Project"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(project.created_at)}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Referral Summary Card */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5" />
                  <span className="font-semibold">Refer & Earn</span>
                </div>
                <p className="text-2xl font-bold">£{referrals.rewards?.total_earned || 0}</p>
                <p className="text-sm opacity-90">earned so far</p>
                {referrals.rewards?.pending_payout > 0 && (
                  <p className="text-sm mt-1 bg-white/20 rounded px-2 py-1 inline-block">
                    £{referrals.rewards.pending_payout} pending
                  </p>
                )}
                <Button 
                  size="sm" 
                  className="w-full mt-3 bg-white text-green-600 hover:bg-gray-100"
                  onClick={() => { setActiveTab("referrals"); }}
                >
                  Refer a Friend
                </Button>
              </div>

              {/* Quick Help */}
              <div className="bg-white rounded-2xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
                <div className="space-y-2">
                  <a href="tel:07547393455" className="block p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <p className="text-sm font-medium text-gray-900">Call Us</p>
                    <p className="text-xs text-gray-500">07547 393 455</p>
                  </a>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Tab Navigation */}
              <div className="bg-white rounded-2xl p-2 flex flex-wrap gap-1">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      activeTab === tab.id 
                        ? "bg-primary text-white" 
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>

              {selectedProject && activeTab === "overview" && (
                <>
                  {/* Project Status Timeline */}
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Project Status</h3>
                    <div className="relative">
                      <div className="absolute top-4 left-0 right-0 h-1 bg-gray-200 rounded" />
                      <div 
                        className="absolute top-4 left-0 h-1 bg-primary rounded transition-all"
                        style={{ width: `${(getStatusIndex(selectedProject.status) / (STATUS_TIMELINE.length - 1)) * 100}%` }}
                      />
                      <div className="relative flex justify-between">
                        {STATUS_TIMELINE.map((step, i) => {
                          const isComplete = i <= getStatusIndex(selectedProject.status);
                          const isCurrent = i === getStatusIndex(selectedProject.status);
                          return (
                            <div key={step.key} className="flex flex-col items-center" style={{ width: "16.66%" }}>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                                isComplete 
                                  ? "bg-primary text-white" 
                                  : "bg-gray-200 text-gray-400"
                              } ${isCurrent ? "ring-4 ring-primary/20" : ""}`}>
                                {isComplete ? <CheckCircle className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
                              </div>
                              <p className={`text-xs mt-2 text-center ${isComplete ? "text-primary font-medium" : "text-gray-400"}`}>
                                {step.label}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Clickable Summary Cards */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* Quote Card - Clickable */}
                    <button 
                      onClick={() => setShowQuoteDetail(selectedProject)}
                      className="bg-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
                      data-testid="quote-summary-card"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">Quote Summary</h3>
                          <p className="text-sm text-gray-500">{selectedProject.project_type?.replace(/_/g, " ")}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Estimated Range</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(selectedProject.estimate_min)} - {formatCurrency(selectedProject.estimate_max)}
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Appointment Card - Clickable */}
                    <button 
                      onClick={() => setShowAppointmentDetail(selectedProject)}
                      className="bg-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
                      data-testid="appointment-summary-card"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">Appointment</h3>
                          <p className="text-sm text-gray-500">Site visit details</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date</span>
                          <span className="font-semibold text-gray-900">
                            {selectedProject.booking_date ? formatDate(selectedProject.booking_date) : "Not scheduled"}
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Payment Summary - Clickable */}
                  <button 
                    onClick={() => setActiveTab("payments")}
                    className="w-full bg-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
                    data-testid="payment-summary-card"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <PoundSterling className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Payment Summary</h3>
                          <p className="text-sm text-gray-500">Click to view details</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                    
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-sm text-gray-600">Job Value</p>
                        <p className="text-xl font-bold text-gray-900">
                          {formatCurrency(selectedProject.payment_summary?.job_value)}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <p className="text-sm text-green-600">Paid</p>
                        <p className="text-xl font-bold text-green-700">
                          {formatCurrency(selectedProject.payment_summary?.total_paid)}
                        </p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-4 text-center">
                        <p className="text-sm text-amber-600">Balance Due</p>
                        <p className="text-xl font-bold text-amber-700">
                          {formatCurrency(selectedProject.payment_summary?.balance_due)}
                        </p>
                      </div>
                    </div>
                  </button>
                </>
              )}

              {/* Quotes Tab */}
              {activeTab === "quotes" && selectedProject && (
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Quote Details</h3>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-600">Project Type</p>
                        <p className="font-semibold text-gray-900">{selectedProject.project_type?.replace(/_/g, " ")}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-semibold text-gray-900">{selectedProject.postcode || "N/A"}</p>
                      </div>
                      <div className="p-4 bg-primary/10 rounded-xl">
                        <p className="text-sm text-primary">Minimum Estimate</p>
                        <p className="text-2xl font-bold text-primary">{formatCurrency(selectedProject.estimate_min)}</p>
                      </div>
                      <div className="p-4 bg-primary/10 rounded-xl">
                        <p className="text-sm text-primary">Maximum Estimate</p>
                        <p className="text-2xl font-bold text-primary">{formatCurrency(selectedProject.estimate_max)}</p>
                      </div>
                    </div>
                    {selectedProject.notes && (
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-600 mb-1">Project Notes</p>
                        <p className="text-gray-900">{selectedProject.notes}</p>
                      </div>
                    )}
                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-500">
                        Quote received: {formatDate(selectedProject.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payments Tab */}
              {activeTab === "payments" && selectedProject && (
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Payment Details</h3>

                  <div className="mb-6">
                    <BankTransferDetails amount={selectedProject.payment_summary?.balance_due} reference={`DCT-${(selectedProject.id || "").substring(0,8).toUpperCase()}`} />
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-gray-600">Job Value</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(selectedProject.payment_summary?.job_value)}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-green-600">Total Paid</p>
                      <p className="text-2xl font-bold text-green-700">
                        {formatCurrency(selectedProject.payment_summary?.total_paid)}
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-amber-600">Balance Due</p>
                      <p className="text-2xl font-bold text-amber-700">
                        {formatCurrency(selectedProject.payment_summary?.balance_due)}
                      </p>
                    </div>
                  </div>

                  <h4 className="font-medium text-gray-700 mb-3">Payment History</h4>
                  {selectedProject.payments?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedProject.payments.map(payment => (
                        <div 
                          key={payment.payment_id} 
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100"
                          onClick={() => setShowPaymentDetail(payment)}
                          data-testid={`payment-${payment.payment_id}`}
                        >
                          <div>
                            <p className="font-medium text-gray-900">
                              {payment.payment_type?.replace(/_/g, " ")}
                            </p>
                            <p className="text-sm text-gray-500">{formatDate(payment.created_at)}</p>
                          </div>
                          <div className="text-right flex items-center gap-4">
                            <div>
                              <p className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                              <p className={`text-sm ${payment.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>
                                {payment.payment_status}
                              </p>
                            </div>
                            {payment.payment_status === "pending" && (
                              <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full" data-testid="bank-transfer-pending">
                                Awaiting Bank Transfer
                              </span>
                            )}
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-400 py-8">No payments recorded yet</p>
                  )}
                </div>
              )}

              {/* Messages Tab */}
              {activeTab === "messages" && selectedProject && (
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Messages</h3>
                      <p className="text-sm text-gray-500">Send notes or questions to the team</p>
                    </div>
                  </div>

                  {selectedProject.messages?.length > 0 && (
                    <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
                      {selectedProject.messages.map(msg => (
                        <div
                          key={msg.id}
                          className={`p-4 rounded-xl ${
                            msg.from_customer ? "bg-primary/10 ml-8" : "bg-gray-50 mr-8"
                          }`}
                        >
                          <p className="text-gray-900">{msg.message}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {msg.from_customer ? "You" : "DC Tiling"} • {formatDate(msg.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1"
                      rows={2}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      className="bg-primary hover:bg-primary/90 self-end"
                    >
                      {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {/* Quote History Tab */}
              {activeTab === "history" && selectedProject && (
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <History className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Quote History</h3>
                      <p className="text-sm text-gray-500">View all quote versions for this project</p>
                    </div>
                  </div>

                  {loadingHistory ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : quoteHistory.length > 0 ? (
                    <div className="space-y-3">
                      {quoteHistory.map((version, idx) => (
                        <div 
                          key={version.id}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                            idx === 0 ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => setShowQuoteDetail(version)}
                          data-testid={`quote-version-${version.version}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">Version {version.version}</span>
                                {idx === 0 && (
                                  <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full">Current</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">Issued: {formatDate(version.issued_at)}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-900">
                                {formatCurrency(version.estimate_min)} - {formatCurrency(version.estimate_max)}
                              </p>
                              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                            </div>
                          </div>
                          {version.description && (
                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{version.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-400 py-8">No quote history available</p>
                  )}
                </div>
              )}

              {/* Referrals Tab */}
              {activeTab === "referrals" && (
                <div className="space-y-6">
                  {/* Referral Stats */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="bg-white rounded-2xl p-4 text-center">
                      <p className="text-sm text-gray-600">Total Referrals</p>
                      <p className="text-2xl font-bold text-gray-900">{referrals.counts?.total || 0}</p>
                    </div>
                    <div className="bg-green-50 rounded-2xl p-4 text-center">
                      <p className="text-sm text-green-600">Successful</p>
                      <p className="text-2xl font-bold text-green-700">{referrals.counts?.successful || 0}</p>
                    </div>
                    <div className="bg-amber-50 rounded-2xl p-4 text-center">
                      <p className="text-sm text-amber-600">Rewards Earned</p>
                      <p className="text-2xl font-bold text-amber-700">£{referrals.rewards?.total_earned || 0}</p>
                    </div>
                  </div>

                  {/* Referral Form */}
                  <div className="bg-white rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Gift className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Refer a Friend</h3>
                          <p className="text-sm text-gray-500">Earn £100 when they book and pay their deposit</p>
                        </div>
                      </div>
                      {!showReferralForm && (
                        <Button onClick={() => setShowReferralForm(true)}>
                          <Users className="w-4 h-4 mr-2" />
                          Add Referral
                        </Button>
                      )}
                    </div>

                    {showReferralForm && (
                      <ReferralForm 
                        onSubmit={handleSubmitReferral}
                        onCancel={() => setShowReferralForm(false)}
                        submitting={submittingReferral}
                      />
                    )}
                  </div>

                  {/* Referral List */}
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Your Referrals</h3>
                    {referrals.referrals?.length > 0 ? (
                      <div className="space-y-3">
                        {referrals.referrals.map(ref => (
                          <div key={ref.id} className="p-4 bg-gray-50 rounded-xl">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{ref.referred_name}</p>
                                <p className="text-sm text-gray-500">{ref.referred_email}</p>
                              </div>
                              <div className="text-right">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  ref.status === "reward_issued" ? "bg-green-100 text-green-700" :
                                  ref.status === "deposit_paid" ? "bg-amber-100 text-amber-700" :
                                  "bg-gray-100 text-gray-700"
                                }`}>
                                  {ref.status === "reward_issued" ? "Reward Issued" :
                                   ref.status === "deposit_paid" ? "Reward Pending" :
                                   "Pending"}
                                </span>
                                <p className="text-sm text-gray-500 mt-1">{formatDate(ref.created_at)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-400 py-8">No referrals yet. Start referring friends to earn rewards!</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quote Detail Modal */}
      {showQuoteDetail && (
        <DetailModal title="Quote Details" onClose={() => setShowQuoteDetail(null)}>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600">Project Type</p>
                <p className="font-semibold text-gray-900">
                  {showQuoteDetail.project_type?.replace(/_/g, " ") || "Project"}
                </p>
              </div>
              {showQuoteDetail.version && (
                <div className="p-4 bg-primary/10 rounded-xl">
                  <p className="text-sm text-primary">Version</p>
                  <p className="font-semibold text-primary">Version {showQuoteDetail.version}</p>
                </div>
              )}
            </div>
            <div className="p-4 bg-primary/10 rounded-xl">
              <p className="text-sm text-primary">Estimated Price Range</p>
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(showQuoteDetail.estimate_min || showQuoteDetail.estimated_min)} - {formatCurrency(showQuoteDetail.estimate_max || showQuoteDetail.estimated_max)}
              </p>
            </div>
            {(showQuoteDetail.description || showQuoteDetail.notes) && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600 mb-1">Description</p>
                <p className="text-gray-900">{showQuoteDetail.description || showQuoteDetail.notes}</p>
              </div>
            )}
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600">Issued</p>
              <p className="font-medium text-gray-900">{formatDate(showQuoteDetail.issued_at || showQuoteDetail.created_at)}</p>
            </div>
          </div>
        </DetailModal>
      )}

      {/* Appointment Detail Modal */}
      {showAppointmentDetail && (
        <DetailModal title="Appointment Details" onClose={() => setShowAppointmentDetail(null)}>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-xl">
              <p className="text-sm text-green-600">Scheduled Date</p>
              <p className="text-xl font-bold text-green-700">
                {showAppointmentDetail.booking_date ? formatDate(showAppointmentDetail.booking_date) : "Not yet scheduled"}
              </p>
            </div>
            {showAppointmentDetail.booking_time && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600">Time</p>
                <p className="font-semibold text-gray-900">{showAppointmentDetail.booking_time}</p>
              </div>
            )}
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-semibold text-gray-900">{showAppointmentDetail.postcode || "N/A"}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-blue-600 mb-2">What to expect</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Our tiler will assess your space</li>
                <li>• Discuss your requirements and preferences</li>
                <li>• Take measurements if needed</li>
                <li>• Provide a detailed quote after the visit</li>
              </ul>
            </div>
          </div>
        </DetailModal>
      )}

      {/* Payment Detail Modal */}
      {showPaymentDetail && (
        <DetailModal title="Payment Details" onClose={() => setShowPaymentDetail(null)}>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600">Payment Type</p>
              <p className="font-semibold text-gray-900">{showPaymentDetail.payment_type?.replace(/_/g, " ")}</p>
            </div>
            <div className="p-4 bg-primary/10 rounded-xl">
              <p className="text-sm text-primary">Amount</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(showPaymentDetail.amount)}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600">Status</p>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                showPaymentDetail.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
              }`}>
                {showPaymentDetail.payment_status}
              </span>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600">Created</p>
              <p className="font-medium text-gray-900">{formatDate(showPaymentDetail.created_at)}</p>
            </div>
            {showPaymentDetail.notes && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600">Notes</p>
                <p className="text-gray-900">{showPaymentDetail.notes}</p>
              </div>
            )}
            {showPaymentDetail.payment_reference && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600">Payment Reference</p>
                <p className="font-semibold text-gray-900 font-mono">{showPaymentDetail.payment_reference}</p>
              </div>
            )}
            {showPaymentDetail.payment_status === "pending" && (
              <BankTransferDetails amount={showPaymentDetail.amount} reference={showPaymentDetail.payment_reference} />
            )}
          </div>
        </DetailModal>
      )}
    </div>
  );
};

export default CustomerPortal;
